import LanguageIcon from "@mui/icons-material/Language";
import { Button, Chip, Popover } from "@mui/material";
import React, {
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
// import CoreChip from "../../../core/components/CoreChip/CoreChip";
import { useSelector } from "react-redux";
import CoreFilterButtonIcon from "../../../core/components/CoreFilterButtonIcon/CoreFilterButtonIcon";

// Interfaccia per l'oggetto filtro
// export interface Filter {
//     label?: string;
//     key: string;
//     value: string;
//     global?: boolean;
// }

// // Interfaccia per i componenti figli che emettono filtri
// export interface FilterComponentProps {
//     onFilterChange: (filter: Filter | null) => void; // null per rimuovere il filtro
//     onFilterReset?: () => void; // callback per reset
//     currentFilter?: Filter; // filtro attualmente attivo per questo componente
// }

// // Props del componente CoreFilter
// interface CoreFilterProps {
//     children?: React.ReactNode;
//     onFiltersChange?: (filters: Filter[]) => void; // Callback unica per emettere tutti i filtri
//     initialFilters?: Filter[];
// }

export default function CoreFilter({
  children,
  onFiltersChange,
  onGlobalFiltersApply,
  initialFilters = [
    // { label: "mario", key: "adminId", value: '0d553097-7a01-4991-acaf-db23705115eb', global:true },
    // { label: "ClienteYUU", key: "userId", value: "2" },
  ],
}) {
  // console.log("CoreFilter rendered with initial filters:", initialFilters);

  const { t } = useTranslation();
  const colorPrimary = useSelector((state) => state.app.primaryColor);

  const [filters, setFilters] = useState(initialFilters);
  const [anchorEl, setAnchorEl] = useState(null);
  // Nuovo stato per tracciare se i filtri globali sono stati applicati
  const [globalFiltersApplied, setGlobalFiltersApplied] = useState(false);

  const globalFilters = filters.filter((filter) => filter.global);
  const globalFiltersApplyDisabled =
    globalFilters.length === 0 || globalFiltersApplied;

  useEffect(() => {
    if (initialFilters && initialFilters.length > 0) {
      setGlobalFiltersApplied(initialFilters.some((filter) => filter.global));
    }
  }, [initialFilters]);

  // Funzione per gestire i cambiamenti di filtro dai componenti figli
  const handleFilterChange = (filter) => {
    // Se filter è null o undefined, non fare nulla
    if (!filter) return;

    // Se il valore è undefined o vuoto, rimuovi il filtro
    if (filter.value === undefined) {
      removeFilter(filter.key);
      return;
    }

    setFilters((prevFilters) => {
      const existingFilterIndex = prevFilters.findIndex(
        (f) => f.key === filter.key
      );
      let updatedFilters;

      if (existingFilterIndex >= 0) {
        // Sostituisce il filtro esistente
        updatedFilters = prevFilters.map((f, index) =>
          index === existingFilterIndex
            ? { ...filter, global: filter.global || false }
            : f
        );
      } else {
        // Aggiunge nuovo filtro
        updatedFilters = [
          ...prevFilters,
          { ...filter, global: filter.global || false },
        ];
      }

      // Emette i filtri aggiornati
      onFiltersChange(updatedFilters);
      // console.log(updatedFilters);

      return updatedFilters;
    });
  };

  // Rimozione filtro usando la key
  const removeFilter = (key) => {
    setFilters((prevFilters) => {
      const filterRemoved = prevFilters.find((filter) => filter.key === key);
      const updatedFilters = prevFilters.filter((filter) => filter.key !== key);
      if (filterRemoved && filterRemoved.global) {
        // Se il filtro rimosso era globale, notifica il reset con valore undefined
        onGlobalFiltersApply([{ key: filterRemoved.key, value: undefined }]);
        setGlobalFiltersApplied(false); // Reset dello stato globale
      }
      onFiltersChange(updatedFilters);
      return updatedFilters;
    });
  };

  // Funzione per trovare un filtro per key
  const getFilterByKey = (key) => {
    return filters.find((filter) => filter.key === key);
  };

  // Reset tutti i filtri - emette array vuoto
  const resetAllFilters = () => {
    if (filters.length === 0) return; // Se non ci sono filtri, non fare nulla

    // Se i filtri globali erano applicati, notifica il reset con valori undefined
    if (globalFiltersApplied) {
      const globalFiltersToReset = globalFilters.map((filter) => ({
        key: filter.key,
        value: undefined,
      }));
      onGlobalFiltersApply(globalFiltersToReset);
    }

    setFilters([]);
    setGlobalFiltersApplied(false); // Reset dello stato globale
    onFiltersChange([]); // Emette 0 filtri per notificare il reset
  };

  // Reset solo filtri non globali
  // const resetLocalFilters = () => {
  //     setFilters(prevFilters => {
  //         const updatedFilters = prevFilters.filter(filter => filter.global);
  //         onFiltersChange(updatedFilters);
  //         return updatedFilters;
  //     });
  // };

  // Applica filtri globali - emette tutti i filtri e segna come applicati
  const applyGlobalFilters = () => {
    setGlobalFiltersApplied(true);
    onGlobalFiltersApply(globalFilters);
    setAnchorEl(null);
  };

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  // Clona i componenti figli passando le props necessarie
  const enhancedChildren = React.Children.map(children, (child) => {
    if (isValidElement(child)) {
      const childKey = child.props.filterKey || child.key;
      return cloneElement(child, {
        onFilterChange: handleFilterChange,
        onFilterReset: resetAllFilters,
        currentFilter: childKey ? getFilterByKey(childKey) : undefined,
        disabled: child.props.allowGlobal && globalFiltersApplied,
      });
    }
    return child;
  });

  return (
    <div className="flex items-center gap-2">
      {filters.map((filter) => (
        <Chip
          key={filter.key}
          label={`${t(`filter_labels.${filter.key}`, filter.key)}: ${
            filter.label || filter.value
          }`}
          variant="outlined"
          onDelete={() => removeFilter(filter.key)}
          // Le chips globali applicate diventano blu
          sx={
            filter.global && globalFiltersApplied
              ? {
                  "&.MuiChip-root": {
                    borderColor: "#1d4ed8",
                    color: "#1d4ed8",
                    "& .MuiChip-deleteIcon": {
                      color: "#1d4ed8",
                    },
                  },
                }
              : {
                  "&.MuiChip-root": {
                    borderColor: colorPrimary,
                    color: colorPrimary,
                    "& .MuiChip-deleteIcon": {
                      color: colorPrimary,
                    },
                  },
                }
          }
        />
      ))}

      <CoreFilterButtonIcon onOpen={handleOpenPopover} />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { borderRadius: "8px", marginTop: "5px", overflow: "visible" },
          },
        }}
      >
        <div className="popover-content p-2">
          <div className="filter-components-wrapper">{enhancedChildren}</div>
          <div className="flex justify-end p-2 gap-x-4">
            <Button
              className="bg-red-700 text-white min-w-[6rem]"
              onClick={resetAllFilters}
            >
              Reset
            </Button>
            <Button
              className="bg-blue-700 text-white gap-x-2"
              disabled={globalFiltersApplyDisabled}
              sx={{
                opacity: globalFiltersApplyDisabled ? 0.5 : 1, // Riduce l'opacità quando disabilitato
              }}
              onClick={applyGlobalFilters}
            >
              <LanguageIcon fontSize="small" />
              <span>Applica Filtri Globali</span>
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
